/**
 * Minimal `chrome.*` API shim for the demo harness.
 *
 * Only implements the surface area the Pearl shell + content bootstrap
 * actually use:
 *   - `chrome.storage.local` / `chrome.storage.sync` (in-memory, callback API)
 *   - `chrome.runtime.sendMessage` / `onMessage` / `getURL`
 *
 * Storage is in-memory (NOT `localStorage`) so refreshing the demo page
 * yields a fresh state and the host page's localStorage is never touched.
 *
 * `sendMessage` handles `SCRIBE_OPEN_TAB` and `PEARL_FETCH_CONFIG` inline,
 * replacing the background service worker. Everything else resolves to
 * `undefined` so callers that fire-and-forget don't break.
 *
 * `simulateLogin(token, expiresAt)` dispatches `PEARL_LOGIN_DETECTED` to
 * every registered `runtime.onMessage` listener — the demo toolbar uses
 * this to put the shell into the "connected" state with feature flags.
 */

import { decodeJwtPayload, extractOrganizationId } from '../../src/services/jwt-decode';
import { fetchFeatureFlags, type FeatureFlags } from '../../src/services/configuration';
import { MSG } from '../../src/contracts/index';

type StorageCallback = (data: Record<string, unknown>) => void;
type RuntimeListener = (
  message: unknown,
  sender: Record<string, unknown>,
  sendResponse: (response?: unknown) => void,
) => boolean | void | unknown;

interface StorageAreaShim {
  get(keys: Record<string, unknown>, cb: StorageCallback): void;
  set(items: Record<string, unknown>, cb?: () => void): void;
  remove(key: string, cb?: () => void): void;
}

function makeStorageArea(store: Record<string, unknown>): StorageAreaShim {
  return {
    get(keys, cb) {
      const result: Record<string, unknown> = {};
      for (const [k, def] of Object.entries(keys)) {
        result[k] = k in store ? store[k] : def;
      }
      // Use a microtask to mimic chrome's async callback dispatch — keeps
      // ordering stable for code that schedules work after a get.
      Promise.resolve().then(() => cb(result));
    },
    set(items, cb) {
      Object.assign(store, items);
      if (cb) Promise.resolve().then(cb);
    },
    remove(key, cb) {
      delete store[key];
      if (cb) Promise.resolve().then(cb);
    },
  };
}

const localStore: Record<string, unknown> = {};
const syncStore: Record<string, unknown> = {};
const messageListeners: RuntimeListener[] = [];

/**
 * Dispatch a message to every registered runtime.onMessage listener.
 * Mirrors how `chrome.tabs.sendMessage` would deliver to a content script.
 */
function dispatchRuntimeMessage(message: unknown): void {
  for (const listener of messageListeners.slice()) {
    try {
      listener(message, {}, () => {});
    } catch (err) {
      console.error('[demo-shim] runtime.onMessage listener threw:', err);
    }
  }
}

/**
 * Inline implementation of the background service worker's
 * `PEARL_FETCH_CONFIG` handler. Decodes the JWT, calls the configuration
 * API directly with a real `fetch`, and resolves with the same shape the
 * real router uses.
 */
async function handleFetchConfig(accessToken: string): Promise<{
  ok: boolean;
  featureFlags?: FeatureFlags;
  error?: string;
}> {
  if (!accessToken) return { ok: false, error: 'No access token' };
  try {
    const flags = await fetchFeatureFlags(
      accessToken,
      decodeJwtPayload,
      extractOrganizationId,
    );
    if (!flags) return { ok: false, error: 'No organization ID in token' };
    return { ok: true, featureFlags: flags };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Config fetch failed';
    console.warn('[demo-shim] FETCH_CONFIG failed (likely CORS):', msg);
    return { ok: false, error: msg };
  }
}

/**
 * The shim exposed to the rest of the demo. Cast to `typeof chrome` at the
 * call site — only the methods listed below are ever invoked, so we don't
 * model the full `chrome` API.
 */
export const chromeShim = {
  storage: {
    local: makeStorageArea(localStore),
    sync: makeStorageArea(syncStore),
  },
  runtime: {
    /**
     * Promisified chrome.runtime.sendMessage. Handles a small set of
     * message types inline (replacing the background worker) and resolves
     * `undefined` for anything else so fire-and-forget callers see a
     * benign no-op.
     */
    sendMessage(message: unknown, callback?: (response?: unknown) => void): Promise<unknown> {
      const m = (message || {}) as { type?: string; url?: string; accessToken?: string };
      const promise = (async () => {
        if (m.type === MSG.OPEN_TAB && m.url) {
          window.open(m.url, '_blank');
          return { success: true };
        }
        if (m.type === MSG.FETCH_CONFIG) {
          return handleFetchConfig(m.accessToken || '');
        }
        // IFRAME_ACTION, AUTH_TOKEN_FOUND, etc — accept silently.
        return { ok: true };
      })();
      if (callback) {
        promise.then((response) => {
          try { callback(response); } catch (err) { console.error('[demo-shim] sendMessage callback threw:', err); }
        });
      }
      return promise;
    },
    onMessage: {
      addListener(listener: RuntimeListener): void {
        messageListeners.push(listener);
      },
      removeListener(listener: RuntimeListener): void {
        const idx = messageListeners.indexOf(listener);
        if (idx >= 0) messageListeners.splice(idx, 1);
      },
    },
    /**
     * Resolves an extension-relative path to a URL the demo can load.
     * Inside the extension this returns `chrome-extension://.../<path>`;
     * here we use a path from the web server root so it works for both
     * `demo/index.html` and `demo/ascend/index.html` (relative `../icons`
     * would incorrectly resolve to `/demo/icons/...` from ascend).
     *
     * Expect `npm run demo:serve` (or similar) with the repo root as the
     * server document root so `/icons/...` maps to `icons/` in the project.
     */
    getURL(path: string): string {
      const clean = path.replace(/^\/+/, '');
      return `/${clean}`;
    },
  },
};

/**
 * Dispatch a `PEARL_LOGIN_DETECTED` message to all registered runtime
 * listeners. The demo toolbar wires its "Simulate Login" button to this.
 *
 * @param accessToken - JWT access token to forward to listeners.
 * @param expiresAt - Token expiry timestamp (ms since epoch).
 */
export function simulateLogin(accessToken: string, expiresAt: number): void {
  dispatchRuntimeMessage({ type: MSG.LOGIN_DETECTED, accessToken, expiresAt });
}

/**
 * Imperative reset — clears in-memory storage and runtime listeners.
 * Useful if the demo toolbar offers a "reset" affordance.
 */
export function resetShim(): void {
  for (const k of Object.keys(localStore)) delete localStore[k];
  for (const k of Object.keys(syncStore)) delete syncStore[k];
  messageListeners.length = 0;
}
